<script setup>

import {storeToRefs} from "pinia";
import {showConfirmDialog} from "vant";
import {useUserAdvocateStore} from "@/stores/userAdvocate.store";
import {onMounted} from "vue";
import {useUserAdvocatePatientStore} from "@/stores/userAdvocatePatient.store";
import {useGlobalStore} from "@/stores/global.store";

const userAdvocateStore = useUserAdvocateStore();
const userAdvocatePatientStore = useUserAdvocatePatientStore();
const globalStore = useGlobalStore();
const {userAdvocates, status, search} = storeToRefs(userAdvocateStore);
const {userAdvocatePatients} = storeToRefs(userAdvocatePatientStore);

onMounted(async () => {
    globalStore.loading = true;
    await userAdvocateStore.fetchAll();
    await userAdvocatePatientStore.fetchAllPatients();
    globalStore.loading = false;
});

const doSearch = async () => {
    await userAdvocateStore.fetchAll();
}

const updateStatus = async (status) => {
    userAdvocateStore.status = status;
    if (status === '') {
        userAdvocateStore.status = '';
    }
    await userAdvocateStore.fetchAll();
}

const doDelete = async (id) => {
    showConfirmDialog({
        title: 'Notice',
        message: 'Do you want to delete this entry?',
    }).then(async () => {
        await userAdvocateStore.deleteOne(id);
        await doSearch();
    });
}

</script>

<template>
    <van-space direction="vertical" fill>
        <van-tabs>
            <van-tab title="Advocates Request">
                <van-search v-model="search" @search="doSearch" @cancel="doSearch"
                            placeholder="Search for your advocates"/>
                <van-grid clickable :column-num="4">
                    <van-grid-item icon="filter-o" text="All" :dot="status === ''" @click="updateStatus('')"/>
                    <van-grid-item icon="todo-list-o" text="Pending" :dot="status === 'pending'"
                                   @click="updateStatus('pending')"/>
                    <van-grid-item icon="passed" text="Approved" :dot="status === 'approved'"
                                   @click="updateStatus('approved')"/>
                    <van-grid-item icon="close" text="Disabled" :dot="status === 'disabled'"
                                   @click="updateStatus('disabled')"/>
                </van-grid>

                <div v-if="userAdvocates.length === 0">
                    <van-empty image="search" description="Nothing found"/>
                </div>
                <div v-if="userAdvocates.length > 0">
                    <van-cell-group inset
                                    v-for="userAdvocate in userAdvocates" :key="userAdvocate.id">
                        <van-cell is-link :to="'/advocate/' + userAdvocate.id">
                            <template #title>
                                <b>{{ userAdvocate.full_name }}</b>
                            </template>
                        </van-cell>
                        <van-cell title="Relation" :value="userAdvocate.relation"/>
                        <van-cell title="Email" :value="userAdvocate.email"/>
                        <van-cell title="Status" :value="userAdvocate.status"/>
                        <van-cell title="Created" title-class="" :value="userAdvocate.created_at_formatted"/>
                        <van-cell center title="Action">
                            <van-button icon="delete-o" size="mini" plain type="danger"
                                        @click="doDelete(userAdvocate.id)"/>
                        </van-cell>
                        <van-row>&nbsp;</van-row>
                    </van-cell-group>
                </div>

                <van-cell-group inset>
                    <van-button block round type="success" to="/advocate/0">
                        New Advocate
                    </van-button>
                </van-cell-group>
            </van-tab>
            <van-tab title="My Patients">
                <div v-if="userAdvocatePatients.length === 0">
                    <van-empty description="Empty"/>
                </div>
                <div v-if="userAdvocatePatients.length > 0">
                  <span v-for="patient in userAdvocatePatients" :key="patient.id">
                        <van-cell-group inset>
                            <van-cell title="Name" :value="patient.full_name"/>
                            <van-cell title="Account" :value="patient.patient_account.name"/>
                            <van-cell title="Counselor" :value="patient.staff.full_name"/>
                            <van-cell title="Sober At" :value="patient.sober_at_formatted"/>
                            <van-cell title="Reports" is-link :to="'/advocate/report/' + patient.id"/>
                        </van-cell-group>
                        <van-row>&nbsp;</van-row>
                  </span>
                </div>


            </van-tab>
        </van-tabs>
    </van-space>
</template>

<style>

:root:root {
    --van-grid-item-text-font-size: var(--van-font-size-xs);
}

</style>
